/**
 * Opening a chat AT a message (`?message=` from a Chats search hit or a notification card) reads the
 * conversation's normal LATEST window and then looks for the target in it. A target older than that page
 * is reached the way a reader would reach it — by paging back — and a target that cannot be reached is
 * given up on: the chat settles at the bottom like any other open, with no highlight. There is no
 * separate read-only view of an old window.
 *
 * This is the decision, kept pure (no runtime imports — see CLAUDE.md "Tests (jest)") so it is
 * unit-testable. `MessagesPanel` feeds it and acts on the answer; iOS and Android implement the same
 * verdict with the same cap.
 */

/** At most this many older pages are read automatically per open-at-target. */
export const OPEN_TARGET_PAGE_CAP = 10;

/**
 * - `found` — the target row is drawn: land on it (and highlight it).
 * - `wait` — not decidable yet: the open read has not landed, or a page is in flight.
 * - `page` — read the next older page.
 * - `give-up` — the target will not appear: stop waiting, settle at the bottom, no highlight.
 */
export type TargetSearchVerdict = 'found' | 'wait' | 'page' | 'give-up';

export interface TargetSearchState {
  /** The target is among the conversation's loaded messages. */
  found: boolean;
  /** …and the panel actually draws it. A row the panel filters out can never be landed on. Default: `found`. */
  drawn?: boolean;
  /** An older page exists (the window has a cursor). Always false on the legacy (chats-off) read. */
  hasOlder: boolean;
  /** A page is being read, or something else makes paging impossible right now (our own turn streaming). */
  loadingOlder: boolean;
  /** The conversation's open read has LANDED for this selection — a cached paint does not count. */
  loaded: boolean;
  /** The open read failed: nothing more will arrive. */
  failed?: boolean;
  /** Older pages already requested for this open-at-target. */
  pagesLoaded: number;
  cap?: number;
}

export function targetSearchVerdict({
  found, drawn = found, hasOlder, loadingOlder, loaded, failed = false, pagesLoaded, cap = OPEN_TARGET_PAGE_CAP,
}: TargetSearchState): TargetSearchVerdict {
  if (found) return drawn ? 'found' : 'give-up';
  if (failed) return 'give-up';
  if (!loaded || loadingOlder) return 'wait';
  if (hasOlder && pagesLoaded < cap) return 'page';
  return 'give-up';
}
